import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { accessSummary } from "../lib/access";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Clock, Users } from "lucide-react";

function RoleBadge({ role }) {
  if (role === "admin" || role === "system_admin")
    return <Badge className="bg-blue-600 text-white">Admin</Badge>;
  if (role === "supervisor")
    return <Badge className="bg-amber-500 text-white">Supervisor</Badge>;
  return <Badge variant="outline" className="text-slate-600">Employee</Badge>;
}

export default function DivisionTeam() {
  const { user } = useAuth();
  const tz = user?.org_timezone;
  const [data, setData] = useState(null);
  const [handoffs, setHandoffs] = useState([]);

  useEffect(() => {
    api.get("/division-team").then((r) => setData(r.data)).catch(() => {});
    api.get("/handoffs").then((r) => setHandoffs(r.data)).catch(() => {});
  }, []);

  const ackHistory = handoffs.flatMap((h) =>
    (h.acknowledgements || []).map((a) => ({ ...a, shift: h.shift_name, division: h.division }))
  ).sort((x, y) => new Date(y.created_at) - new Date(x.created_at));

  return (
    <div className="animate-fade-up space-y-8">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Supervision</p>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Division Team</h1>
        <p className="mt-1 text-sm text-slate-500">
          Read-only view of personnel in the division{(data?.divisions?.length || 0) > 1 ? "s" : ""} you supervise
          {data?.divisions?.length ? `: ${data.divisions.join(", ")}` : ""}.
        </p>
      </div>

      <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <Users className="h-4 w-4 text-slate-500" /> Members
        </h2>
        <div className="mt-3 space-y-2" data-testid="division-team-list">
          {(data?.members || []).map((m) => (
            <div key={m.id} data-testid={`division-member-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-100 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-800 text-xs font-bold text-white">
                  {m.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{m.name}</p>
                  <p className="text-xs text-slate-500">{m.email} · {m.shift_name || "Unassigned"}</p>
                  <p className="mt-0.5 text-[11px] text-slate-400">{accessSummary(m)}</p>
                </div>
              </div>
              <RoleBadge role={m.role} />
            </div>
          ))}
          {(data?.members || []).length === 0 && <p className="text-sm text-slate-400">No personnel in your supervised divisions yet.</p>}
        </div>
        <p className="mt-3 text-xs text-slate-400">This view is read-only. Organization-wide roles and permissions are managed by an administrator.</p>
      </Card>

      <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <Clock className="h-4 w-4 text-slate-500" /> Acknowledgement History
        </h2>
        <div className="mt-3 space-y-2" data-testid="division-ack-history">
          {ackHistory.length === 0 ? (
            <p className="text-sm text-slate-400">No acknowledgements recorded yet.</p>
          ) : ackHistory.map((a, i) => (
            <div key={i} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
              <span className="text-sm text-slate-700"><span className="font-semibold">{a.user_name}</span> acknowledged <span className="font-semibold">{a.shift}</span>{a.division ? ` · ${a.division}` : ""}</span>
              <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString("en-US", { timeZone: tz || "America/Chicago", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
